<template>
  <div ref="root" class="d-flex flex-1" style="height: 100%">
    <!-- LEFT PANE -->
    <div
      class="d-flex ant-border-right radius-0"
      :style="{
        width: Math.round(leftRatio * rootWidth) + 'px',
        height: '100%',
      }"
    >
      <div
        class="d-flex flex-column flex-1 ant-border-right pa-4"
        style="min-width: 0"
      >
        <v-subheader class="pa-0 mb-4" style="height: auto; font-size: 16px">
          Options & Remarks
          <v-spacer />
          <v-tooltip left>
            <template #activator="{ on }">
              <v-icon @click="closeDetailView" v-on="on">mdi-close</v-icon>
            </template>
            <span>Close view</span>
          </v-tooltip>
        </v-subheader>

        <v-alert
          v-if="!loading && orphanTasks.length"
          class="mb-2 fs-12"
          dense
          outlined
          type="warning"
        >
          <div class="d-flex align-center">
            {{ orphanTasks.length }} task(s) found that are no longer part of
            this preparation
            <v-spacer />
            <v-btn x-small text @click="showOrphanTasks = !showOrphanTasks">
              {{ showOrphanTasks ? 'Hide' : 'Show' }}
            </v-btn>
            <v-btn
              v-if="isProjectAdmin"
              class="ml-1"
              color="error"
              x-small
              text
              @click="orphanDeleteDialog = true"
            >
              Remove
            </v-btn>
          </div>
          <div v-if="showOrphanTasks" class="mt-2">
            <div
              v-for="task in orphanTasks"
              :key="task.id"
              class="d-flex align-center fs-10"
            >
              {{ task.title }}
              <span v-if="task.task_type.custom_4" class="font-italic ml-2">{{
                task.task_type.custom_4
              }}</span>
              <v-spacer />
              <span class="font-italic">{{ task.status }}</span>
            </div>
          </div>
        </v-alert>

        <div v-if="!loading" class="overflow-y-auto">
          <div
            v-for="location in assemblyPreparationsHallLocations"
            :key="location.id"
            class="px-2 py-4 ant-border-bottom"
          >
            <v-subheader
              v-if="assemblyPreparationsHall.automated"
              class="pa-0 mb-2"
              style="height: auto"
            >
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-icon
                    :color="isLocationCompleted(location) ? 'success' : ''"
                    :disabled="
                      isLocationCompleted(location) ||
                      locationLoadingStates[location.id]
                    "
                    :class="
                      locationLoadingStates[location.id] ? 'mdi-spin' : ''
                    "
                    class="mr-2"
                    @click="completeLocation(location)"
                    v-on="on"
                  >
                    {{
                      locationLoadingStates[location.id]
                        ? 'mdi-loading'
                        : isLocationCompleted(location)
                          ? 'mdi-check-circle-outline'
                          : 'mdi-circle-outline'
                    }}
                  </v-icon>
                </template>
                <span>{{
                  isLocationCompleted(location)
                    ? 'Location completed'
                    : 'Complete location'
                }}</span>
              </v-tooltip>
              Location {{ location.assembly_location }} -
              {{ location.description }}
              <v-tooltip v-if="canReopenLocation(location)" bottom>
                <template #activator="{ on }">
                  <v-btn
                    class="ml-2"
                    icon
                    x-small
                    @click="reopenLocation(location)"
                    v-on="on"
                  >
                    <v-icon small>mdi-restore</v-icon>
                  </v-btn>
                </template>
                <span>Reopen location</span>
              </v-tooltip>
              <v-spacer />
              <v-checkbox
                v-if="!isLocationCompleted(location)"
                v-model="location.toggle"
                :ripple="false"
                class="mt-0 pt-0"
                hide-details
                label="Select all"
                @click="selectAll(location)"
              />
            </v-subheader>

            <v-subheader v-else class="pa-0 mb-2" style="height: auto">
              <v-tooltip bottom>
                <template #activator="{ on }">
                  <v-icon
                    :color="isLocationCompleted(location) ? 'success' : ''"
                    :class="
                      locationLoadingStates[location.id] ? 'mdi-spin' : ''
                    "
                    :disabled="
                      isLocationCompleted(location) ||
                      locationLoadingStates[location.id]
                    "
                    class="mr-2"
                    @click="completeLocation(location)"
                    v-on="on"
                  >
                    {{
                      locationLoadingStates[location.id]
                        ? 'mdi-loading'
                        : isLocationCompleted(location)
                          ? 'mdi-check-circle-outline'
                          : 'mdi-circle-outline'
                    }}
                  </v-icon>
                </template>
                <span>{{
                  isLocationCompleted(location)
                    ? 'Location completed'
                    : 'Complete location'
                }}</span>
              </v-tooltip>
              Location
              <v-tooltip v-if="canReopenLocation(location)" bottom>
                <template #activator="{ on }">
                  <v-btn
                    class="ml-2"
                    icon
                    x-small
                    @click="reopenLocation(location)"
                    v-on="on"
                  >
                    <v-icon small>mdi-restore</v-icon>
                  </v-btn>
                </template>
                <span>Reopen location</span>
              </v-tooltip>
              <v-spacer />
              <v-checkbox
                v-if="!isLocationCompleted(location)"
                v-model="location.toggle"
                :ripple="false"
                class="mt-0 pt-0"
                hide-details
                label="Select all"
                @click="selectAll(location)"
              />
            </v-subheader>

            <div class="d-flex">
              <div class="flex-1 mr-4">
                <div
                  v-for="step in assemblyLocationSteps(location)"
                  :key="`${location.id}-step-${step.id}`"
                >
                  <v-chip class="my-2" small
                    >{{ step.order }} - {{ step.title }}</v-chip
                  >

                  <div
                    v-for="(item, index) in locationActivities[
                      location.id
                    ].activities
                      .filter((a) => a.step === step.id)
                      .sort((a, b) => a.order - b.order)"
                    :key="index"
                    class="d-flex align-center fs-12"
                  >
                    <v-menu
                      v-if="
                        item.deviation &&
                        !item.existingTask &&
                        !isLocationCompleted(location)
                      "
                      v-model="deviationOrderMenus[item.id]"
                      :close-on-content-click="false"
                      :nudge-width="200"
                      offset-x
                    >
                      <template #activator="{ on }">
                        <span
                          class="fs-10 font-italic d-flex text-primary c-pointer"
                          style="width: 30px"
                          v-on="on"
                        >
                          {{ item.order }}
                        </span>
                      </template>
                      <v-card class="pa-2">
                        <ant-input label="Order" top-margin="mt-0">
                          <template #input-field>
                            <v-text-field
                              v-model="item.order"
                              autofocus
                              dense
                              filled
                              hide-details
                              placeholder="0-100"
                              single-line
                              type="number"
                              @keydown.enter="updateOrderToDeviationTask(item)"
                            />
                          </template>
                        </ant-input>
                      </v-card>
                    </v-menu>

                    <span
                      v-else
                      class="fs-10 font-italic d-flex"
                      style="width: 30px"
                      >{{ item.order }}</span
                    >

                    <v-checkbox
                      :id="item.id"
                      :ref="item.id"
                      v-model="item.selected"
                      :disabled="isLocationCompleted(location) || item.existingTask"
                      :ripple="false"
                      class="mt-0 pt-0"
                      dense
                      hide-details
                    />

                    <v-icon
                      v-if="item.deviation"
                      class="mr-2"
                      color="warning"
                      small
                    >
                      mdi-alert-rhombus-outline
                    </v-icon>

                    {{ item.activity }}
                    <span v-if="item.position" class="font-italic ml-2 fs-10">{{
                      item.position
                    }}</span>

                    <v-spacer />

                    <v-tooltip bottom>
                      <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on">{{
                          item?.leanforms_q_nr
                        }}</span>
                      </template>
                      <span>{{ item.leanforms_remaining_point ?? '-' }}</span>
                    </v-tooltip>
                  </div>

                  <div
                    v-if="!isLocationCompleted(location)"
                    class="d-flex align-center"
                  >
                    <v-checkbox class="mt-0 pt-0" dense disabled hide-details />
                    <v-menu
                      v-model="
                        locationDeviationMenus[`${location.id}-${step.id}`]
                      "
                      :close-on-content-click="false"
                      :nudge-width="200"
                      offset-x
                    >
                      <template #activator="{ on, attrs }">
                        <v-icon :ripple="false" dense v-bind="attrs" v-on="on"
                          >mdi-plus</v-icon
                        >
                      </template>

                      <v-card class="pa-2">
                        <ant-input label="Option" top-margin="mt-0">
                          <template #input-field>
                            <v-text-field
                              v-model="locationDeviations[location.id]"
                              autofocus
                              dense
                              filled
                              hide-details
                              placeholder="title"
                              single-line
                              @keydown.enter="
                                addLocationDeviation(location, step)
                              "
                            />
                          </template>
                        </ant-input>
                      </v-card>
                    </v-menu>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="d-flex align-center justify-center flex-1">
          <ant-loading />
        </div>
      </div>

      <!-- HORIZONTAL HANDLE -->
      <div
        class="resize-handle-x align-center justify-center d-flex"
        role="separator"
        aria-orientation="vertical"
        :aria-valuenow="Math.round(leftRatio * 100)"
        aria-valuemin="5"
        aria-valuemax="95"
        @mousedown="startHResize"
      >
        <v-icon small>mdi-drag-vertical-variant</v-icon>
      </div>
    </div>

    <!-- RIGHT PANE -->
    <div
      ref="rightPane"
      class="d-flex flex-column flex-1"
      style="height: 100%; min-width: 0"
    >
      <!-- TOP: TABLE -->
      <div
        class="ant-glass-background radius-0"
        :style="{
          height: Math.round(topRatio * rightHeight) + 'px',
          overflow: 'auto',
        }"
      >
        <v-subheader>Elements</v-subheader>
        <v-data-table
          :headers="elementHeaders"
          :items="elements"
          :loading="loading"
          style="background-color: transparent !important"
        />
      </div>

      <!-- VERTICAL HANDLE -->
      <div
        class="resize-handle-y align-center justify-center d-flex"
        role="separator"
        aria-orientation="horizontal"
        :aria-valuenow="Math.round(topRatio * 100)"
        aria-valuemin="5"
        aria-valuemax="95"
        @mousedown="startVResize"
      >
        <v-icon small>mdi-drag-horizontal-variant</v-icon>
      </div>

      <!-- BOTTOM: IFRAME -->
      <div
        class="d-flex align-center justify-center ant-border-top"
        :style="{
          height: Math.round((1 - topRatio) * rightHeight - handleY) + 'px',
          overflow: 'hidden',
        }"
      >
        <iframe
          :src="documentPreview"
          width="100%"
          height="100%"
          type="application/pdf"
          :style="{ pointerEvents: isResizing ? 'none' : 'auto' }"
        />
      </div>
    </div>

    <delete-dialog
      :dialog="orphanDeleteDialog"
      :loading="removingOrphanTasks"
      :title="`Remove ${orphanTasks.length} task(s)?`"
      description="These tasks no longer match an activity of this preparation. They are deleted including their subtasks; this cannot be undone."
      @closeDialog="orphanDeleteDialog = false"
      @deleteAction="removeOrphanTasks"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { DHME_ASSEMBLY_PREPARATIONS } from '@/modules/modules';
import { executeCustomModuleCall } from '@/services/api/module.api';
import AntLoading from '@/components/AntLoading.vue';
import DHMEASMixin from '@/modules/daiwa-house-modular-europe/AssemblyPreparations/DhmeAssemblyPreparations.mixin';
import AntInput from '@/components/AntInput.vue';
import DeleteDialog from '@/components/DeleteDialog.vue';
import { deleteTasks } from '@/services/api/task.api';

const CLAMP_MIN = 0.05;
const CLAMP_MAX = 0.95;
const LS_KEY = 'dhme.ap.details.split.v2';

export default {
  name: 'DhmeAssemblyPreparationsModuleDetails',
  components: { AntInput, AntLoading, DeleteDialog },
  mixins: [DHMEASMixin],
  props: { module: { type: Object, required: true } },
  data: () => ({
    elementHeaders: [
      { text: 'code', value: 'element_type' },
      { text: 'type', value: 'element_category' },
    ],
    selectedActivities: {},
    locationTasks: [],
    elements: [],
    loading: true,
    deviationOrderMenus: {},
    locationActivities: {},
    locationDeviationMenus: {},
    locationDeviations: {},
    documentPreview: null,

    leftRatio: 0.45,
    topRatio: 0.5,
    handleY: 10,

    isResizing: false,
    dragKind: null, // 'h' | 'v'
    dragStartX: 0,
    dragStartY: 0,
    dragStartLeftRatio: 0,
    dragStartTopRatio: 0,

    locationLoadingStates: {},

    // Station tasks of this module that match no current activity, e.g. after
    // the elements were regenerated or an activity was removed
    orphanTasks: [],
    showOrphanTasks: false,
    orphanDeleteDialog: false,
    removingOrphanTasks: false,

    /**
     * LAZY LOADING: Store the fetched assembly task for this module.
     * This replaces the computed property that searched through all cached tasks.
     * The task is fetched on-demand when the module is selected.
     */
    moduleAssemblyTask: null,
    loadingTask: false,
  }),
  computed: {
    ...mapGetters([
      'assemblyPreparationsHall',
      'assemblyPreparationsHallLocations',
      'assemblyPreparationLocationActivities',
      'assemblyPreparationsElements',
      'assemblyPreparationsLocationStatuses',
      'assemblyConfigurationSteps',
      'project',
      'assemblyPreparationLocationTypes',
      'assemblyPreparationsModuleAssemblyTasks',
      'isProjectAdmin',
    ]),
    rootWidth() {
      return this.$refs.root ? this.$refs.root.clientWidth : window.innerWidth;
    },
    rightHeight() {
      return this.$refs.rightPane
        ? this.$refs.rightPane.clientHeight
        : window.innerHeight;
    },
    locationStatuses() {
      return this.assemblyPreparationsLocationStatuses.filter(
        (item) => item.module_id === this.module.module_id
      );
    },
    moduleId() {
      return this.project.modules.find(
        (module) => module.route === DHME_ASSEMBLY_PREPARATIONS
      ).id;
    },
  },
  watch: {
    /**
     * LAZY LOADING: Fetch assembly task when module changes.
     *
     * PREVIOUS BEHAVIOR (removed):
     * - Used a computed property that looked up the task in a pre-loaded cache
     * - Required ALL tasks (1000+) to be fetched upfront on page load
     * - Caused 5-10 second delays and 20MB+ transfers
     *
     * NEW BEHAVIOR:
     * - Fetches only the single task for the selected module
     * - First load: 50-200ms delay (fetch from API)
     * - Subsequent loads: instant (cached in store)
     * - Dramatically reduces initial page load time
     *
     * PERFORMANCE IMPACT:
     * - Page load: 5-10s → <500ms
     * - Module selection: instant → +50-200ms (acceptable tradeoff)
     * - Data transfer: 20MB → 10-200KB per module
     */
    module: {
      immediate: true,
      async handler(module) {
        if (!module) return;

        this.loadingTask = true;
        try {
          // Fetch the task for this specific module (lazy loading)
          const task = await this.$store.dispatch(
            'fetchAssemblyPreparationsModuleAssemblyTaskByBuildNr',
            {
              projectId: this.project.id,
              buildNr: module.build_nr,
            }
          );

          this.moduleAssemblyTask = task;

          // If task found, fetch the 2D drawing
          if (task) {
            await this.fetch2DDrawing(task, this.project.id, module.module_id);
          }
        } catch (error) {
          console.error(
            `Failed to fetch assembly task for module ${module.build_nr}:`,
            error
          );
          this.$store.commit('showNotification', {
            content: 'Failed to load module assembly task',
            color: 'error',
          });
        } finally {
          this.loadingTask = false;
        }
      },
    },
  },
  async mounted() {
    try {
      const raw = localStorage.getItem(LS_KEY);
      if (raw) {
        const { leftRatio, topRatio } = JSON.parse(raw);
        if (typeof leftRatio === 'number')
          this.leftRatio = this.clamp(leftRatio);
        if (typeof topRatio === 'number') this.topRatio = this.clamp(topRatio);
      }
    } catch {}

    await this.fetchTasks();

    this.elements = await executeCustomModuleCall(
      this.project.id,
      this.moduleId,
      'fetchModuleElements',
      { module: this.module.module_id }
    );

    this.assemblyPreparationsHallLocations.forEach((location) => {
      this.locationActivities[location.id] = { activities: [] };

      this.getActivitiesByLocation(
        location,
        this.elements,
        this.assemblyPreparationLocationTypes,
        this.assemblyPreparationLocationActivities
      ).forEach((activity) => {
        if (activity.type && activity.position) {
          const element = this.elements.find(
            (e) =>
              e.module_id === this.module.module_id &&
              e.element_type === activity.type &&
              e.element_position === activity.position
          );
          activity.id = element.element_id;
          activity.sbscode = element.element_id;
        }
        activity.deviation = false;
        // Activities that already have a task are locked, so completing a
        // reopened location only creates tasks for new selections.
        activity.existingTask =
          this.locationTasks.findIndex(
            (task) =>
              task.task_type.custom_2 ===
                (this.moduleAssemblyTask.task_type.custom_3 ?? location.id) &&
              task.task_type.custom_3 === activity.id
          ) !== -1;
        activity.selected = activity.existingTask;
        this.locationActivities[location.id].activities.push(activity);
      });

      const deviationTasks = this.locationTasks.filter(
        (task) =>
          task.task_type.custom_2 ===
            (this.moduleAssemblyTask.task_type.custom_3 ?? location.id) &&
          task.task_type.custom_3 === null
      );

      deviationTasks.forEach((task) => {
        this.locationActivities[location.id].activities.push({
          id: null,
          assembly_location:
            this.moduleAssemblyTask.task_type.custom_3 ?? location.id,
          deviation: true,
          existingTask: true,
          selected: true,
          activity: task.title,
          step: task.task_type.custom_6,
          order: task.task_type.custom_5,
        });
      });
    });

    this.detectOrphanTasks();
    this.loading = false;

    window.addEventListener('resize', this.onWindowResize, { passive: true });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    this.detachDragListeners();
  },
  methods: {
    clamp(v) {
      return Math.min(Math.max(v, CLAMP_MIN), CLAMP_MAX);
    },
    onWindowResize() {
      // nothing required: we compute sizes from refs each render
    },
    persist() {
      try {
        localStorage.setItem(
          LS_KEY,
          JSON.stringify({
            leftRatio: this.leftRatio,
            topRatio: this.topRatio,
          })
        );
      } catch {}
    },

    // Horizontal splitter
    startHResize(e) {
      e.preventDefault();
      this.isResizing = true;
      this.dragKind = 'h';
      this.dragStartX = e.clientX;
      this.dragStartLeftRatio = this.leftRatio;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'e-resize';
      this.attachDragListeners();
    },
    onHDrag(e) {
      const width = this.rootWidth;
      if (!width) return;
      const dx = e.clientX - this.dragStartX;
      const delta = dx / width;
      this.leftRatio = this.clamp(this.dragStartLeftRatio + delta);
      this.persist();
    },

    // Vertical splitter
    startVResize(e) {
      e.preventDefault();
      this.isResizing = true;
      this.dragKind = 'v';
      this.dragStartY = e.clientY;
      this.dragStartTopRatio = this.topRatio;
      document.body.style.userSelect = 'none';
      document.body.style.cursor = 'n-resize';
      this.attachDragListeners();
    },
    onVDrag(e) {
      const height = this.rightHeight;
      if (!height) return;
      const dy = e.clientY - this.dragStartY;
      const delta = dy / height;
      this.topRatio = this.clamp(this.dragStartTopRatio + delta);
      this.persist();
    },

    attachDragListeners() {
      document.addEventListener('mousemove', this.onDrag, { passive: false });
      document.addEventListener('mouseup', this.stopDrag, { passive: false });
    },
    detachDragListeners() {
      document.removeEventListener('mousemove', this.onDrag);
      document.removeEventListener('mouseup', this.stopDrag);
    },
    onDrag(e) {
      e.preventDefault();
      if (!this.isResizing) return;
      if (this.dragKind === 'h') this.onHDrag(e);
      else if (this.dragKind === 'v') this.onVDrag(e);
    },
    stopDrag() {
      this.isResizing = false;
      this.dragKind = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
      this.detachDragListeners();
    },

    // Business logic kept as-is
    async fetchTasks() {
      this.locationTasks = await executeCustomModuleCall(
        this.project.id,
        this.moduleId,
        'fetchAssemblyPrepTasks',
        { module: this.module.module_id }
      );
    },
    selectAll(location) {
      this.locationActivities[location.id].activities.map((item) => {
        item.selected = location.toggle;
        return item;
      });
    },
    assemblyLocationSteps(location) {
      return this.assemblyConfigurationSteps
        .filter((s) =>
          Array.from(
            new Set(
              this.locationActivities[location.id].activities.map((a) => a.step)
            )
          ).includes(s.id)
        )
        .sort((a, b) => parseInt(a.order) - parseInt(b.order));
    },
    updateOrderToDeviationTask(activity) {
      activity.order = parseInt(activity.order);
      this.deviationOrderMenus[activity.id] = false;
    },
    addLocationDeviation(location, step) {
      const generateHash = (locationId, stepId) =>
        `${locationId}-${stepId}-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
      const uniqueId = generateHash(location.id, step.id);
      this.locationActivities[location.id].activities.push({
        id: uniqueId,
        assembly_location: location.id,
        deviation: true,
        selected: true,
        activity: this.locationDeviations[location.id],
        order: 99,
        step: step.id,
      });
      this.locationDeviations[location.id] = null;
      this.locationDeviationMenus[`${location.id}-${step.id}`] = false;
    },
    isLocationCompleted(location) {
      return (
        this.locationStatuses.find(
          (item) =>
            item.assembly_location === location.id &&
            item.status !== 'reopened'
        ) !== undefined
      );
    },
    isLocationReopened(location) {
      return this.locationStatuses.some(
        (item) =>
          item.assembly_location === location.id && item.status === 'reopened'
      );
    },
    // Anyone who can complete a location can reopen it
    canReopenLocation(location) {
      return (
        !this.loading &&
        !this.loadingTask &&
        !!this.moduleAssemblyTask &&
        !this.locationLoadingStates[location.id] &&
        this.isLocationCompleted(location)
      );
    },
    async reopenLocation(location) {
      if (!this.canReopenLocation(location)) return;
      this.$set(this.locationLoadingStates, location.id, true);
      try {
        await this.$store.dispatch('updateLocationStatus', {
          moduleId: this.module.module_id,
          locationId: location.id,
          status: 'reopened',
        });
      } catch (error) {
        this.$store.commit('showNotification', {
          content: error?.message ?? 'Failed to reopen location',
          color: 'error',
        });
      } finally {
        this.$set(this.locationLoadingStates, location.id, false);
      }
    },
    // Uses the same matching as the existing task check on mount: activity id
    // on the location, or a deviation (no activity) on the location. A task
    // that points at an element the module still has is never flagged, since
    // it is offered for deletion.
    detectOrphanTasks() {
      const elementIds = new Set(this.elements.map((e) => e.element_id));
      const matched = new Set();
      this.assemblyPreparationsHallLocations.forEach((location) => {
        const locationKey =
          this.moduleAssemblyTask.task_type.custom_3 ?? location.id;
        const activityIds = new Set(
          this.locationActivities[location.id].activities
            .filter((a) => !a.deviation)
            .map((a) => a.id)
        );
        this.locationTasks.forEach((task) => {
          if (task.task_type.custom_2 !== locationKey) return;
          if (
            task.task_type.custom_3 === null ||
            activityIds.has(task.task_type.custom_3) ||
            elementIds.has(task.task_type.custom_3)
          ) {
            matched.add(task.id);
          }
        });
      });
      this.orphanTasks = this.locationTasks.filter(
        (task) => !matched.has(task.id)
      );
    },
    async removeOrphanTasks() {
      this.removingOrphanTasks = true;
      try {
        await deleteTasks(
          this.orphanTasks.map((t) => ({ id: t.id, method: 'with-subtasks' }))
        );
        await this.fetchTasks();
        this.detectOrphanTasks();
        this.orphanDeleteDialog = false;
        this.$store.commit('showNotification', {
          content: 'Tasks removed',
          color: 'success',
        });
      } catch (error) {
        this.$store.commit('showNotification', {
          content: error?.message ?? 'Failed to remove tasks',
          color: 'error',
        });
      } finally {
        this.removingOrphanTasks = false;
      }
    },
    closeDetailView() {
      this.documentPreview = null;
      this.$emit('closeView');
    },
    async completeLocation(location) {
      if (this.locationLoadingStates[location.id]) return;
      if (!this.moduleAssemblyTask) {
        this.$store.commit('showNotification', {
          content: 'Assembly task not loaded yet.',
          color: 'error',
        });
        return;
      }
      this.$set(this.locationLoadingStates, location.id, true);

      const selectedActivities = this.locationActivities[
        location.id
      ].activities.filter((a) => a.selected);
      // A reopened location already has tasks for these; do not duplicate
      const newActivities = selectedActivities.filter((a) => !a.existingTask);
      const tasks = newActivities.map((a) => ({
        title: a.activity,
        parent: this.moduleAssemblyTask.id,
        due: this.moduleAssemblyTask.due,
        sbscode: a.sbscode ?? null,
        project: this.project.id,
        license: this.project.license,
        options: {
          type: 'dhme-module-assembly-station',
          custom_1: this.module.module_id,
          custom_2: this.moduleAssemblyTask.task_type.custom_3 ?? location.id,
          custom_3: a.deviation ? null : a.id,
          custom_4: a.position ?? null,
          custom_5: a.order ?? 0,
          custom_6: a.step,
          custom_7: a.leanforms_q_nr ?? null,
          custom_8: a.leanforms_remaining_point ?? null,
          custom_9: a.btn_options ?? null,
          metadata: a.translations ?? null,
        },
      }));

      try {
        if (tasks.length) {
          await this.$store.dispatch('importAssemblyPreparationsTasks', tasks);
        }
        const status = selectedActivities.some((t) => t.deviation)
          ? 'alert'
          : 'success';
        if (this.isLocationReopened(location)) {
          // Reuses the reopened status record instead of adding a second one
          await this.$store.dispatch('updateLocationStatus', {
            moduleId: this.module.module_id,
            locationId: location.id,
            status,
          });
        } else {
          await this.$store.dispatch('setLocationStatus', {
            assembly_location: location.id,
            module_id: this.module.module_id,
            status,
          });
        }
        newActivities.forEach((a) => {
          a.existingTask = true;
        });
      } catch (error) {
        console.error('Error completing location:', error);
      } finally {
        this.$set(this.locationLoadingStates, location.id, false);
      }
    },
  },
};
</script>

<style scoped>
/* Handles: thin, but easy to grab */
.resize-handle-x {
  width: 10px;
  cursor: e-resize;
  user-select: none;
}
.resize-handle-y {
  height: 10px;
  cursor: n-resize;
  user-select: none;
}
.resize-handle-x:hover,
.resize-handle-y:hover {
  background: rgba(127, 127, 127, 0.08);
}
</style>
